import { useState, useCallback, useEffect } from "react";
import { SmartDocumentViewer } from "@/components/viewer/SmartDocumentViewer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText, Upload, X, GripVertical, Download, Loader2, FolderOpen, Check, Save, Edit3, RotateCcw, ExternalLink } from "lucide-react";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { toast } from "sonner";
import { useUsageLimits } from "@/hooks/useUsageLimits";
import { UsageLimitBanner } from "@/components/usage/UsageLimitBanner";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { PDFDocument } from "pdf-lib";

interface PDFFile {
  id: string;
  file?: File;
  documentId?: string;
  name: string;
  size: string;
  filePath?: string;
}

interface MergedResult {
  blob: Blob;
  url: string;
  fileName: string;
  documentId?: string;
  filePath?: string;
}

interface PDFMergeProps {
  onRequestSign?: (blob: Blob, fileName: string) => void;
}

const PDFMerge = ({ onRequestSign }: PDFMergeProps) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<PDFFile[]>([]);
  const [processing, setProcessing] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [saveToDocuments, setSaveToDocuments] = useState(true);
  const [existingDocs, setExistingDocs] = useState<Tables<"documents">[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [mergedResult, setMergedResult] = useState<MergedResult | null>(null);
  const { checkLimit, incrementUsage, limits } = useUsageLimits();
  
  // Calculate max docs based on plan
  const maxMergeDocs = limits.merge_docs_max;
  const canAddMoreDocs = maxMergeDocs === -1 || files.length < maxMergeDocs;

  // Fetch existing PDF documents
  const fetchExistingDocs = async () => {
    setLoadingDocs(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("documents")
        .select("*")
        .eq("user_id", user.id)
        .eq("mime_type", "application/pdf")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setExistingDocs(data || []);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoadingDocs(false);
    }
  };

  useEffect(() => {
    fetchExistingDocs();
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const pdfFiles = selectedFiles.filter(f => f.type === 'application/pdf');
    
    if (pdfFiles.length !== selectedFiles.length) {
      toast.error("Seuls les fichiers PDF sont acceptés");
    }
    
    // Check limit before adding
    if (maxMergeDocs !== -1) {
      const remainingSlots = maxMergeDocs - files.length;
      if (remainingSlots <= 0) {
        toast.error(`Limite de ${maxMergeDocs} documents atteinte. Passez à un forfait supérieur.`);
        e.target.value = '';
        return;
      }
      if (pdfFiles.length > remainingSlots) {
        toast.error(`Vous ne pouvez ajouter que ${remainingSlots} document(s) supplémentaire(s)`);
        pdfFiles.splice(remainingSlots);
      }
    }
    
    const newFiles: PDFFile[] = pdfFiles.map(file => ({
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: formatFileSize(file.size)
    }));

    setFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  }, [files.length, maxMergeDocs]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newFiles = [...files];
    const [draggedItem] = newFiles.splice(draggedIndex, 1);
    newFiles.splice(index, 0, draggedItem);
    setFiles(newFiles);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const toggleDocSelection = (docId: string) => {
    setSelectedDocs(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const addSelectedDocs = async () => {
    let docsToAdd = existingDocs.filter(doc => selectedDocs.includes(doc.id));
    
    // Check limit before adding
    if (maxMergeDocs !== -1) {
      const remainingSlots = maxMergeDocs - files.length;
      if (remainingSlots <= 0) {
        toast.error(`Limite de ${maxMergeDocs} documents atteinte. Passez à un forfait supérieur.`);
        setSelectedDocs([]);
        setDocDialogOpen(false);
        return;
      }
      if (docsToAdd.length > remainingSlots) {
        toast.error(`Vous ne pouvez ajouter que ${remainingSlots} document(s) supplémentaire(s)`);
        docsToAdd = docsToAdd.slice(0, remainingSlots);
      }
    }
    
    const newFiles: PDFFile[] = docsToAdd.map(doc => ({
      id: crypto.randomUUID(),
      documentId: doc.id,
      name: doc.file_name,
      size: formatFileSize(doc.file_size || 0),
      filePath: doc.file_path
    }));

    setFiles(prev => [...prev, ...newFiles]);
    setSelectedDocs([]);
    setDocDialogOpen(false);
  };

  const handleMerge = async () => {
    if (files.length < 2) {
      toast.error("Sélectionnez au moins 2 fichiers PDF");
      return;
    }

    if (!checkLimit("pdf_actions")) {
      toast.error("Limite d'actions PDF atteinte. Passez à un plan supérieur.");
      return;
    }

    setProcessing(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // === LOCAL MERGE WITH PDF-LIB (100% client-side) ===
      const mergedPdf = await PDFDocument.create();
      
      for (const pdfFile of files) {
        let pdfBytes: ArrayBuffer;
        
        if (pdfFile.file) {
          // Local file
          pdfBytes = await pdfFile.file.arrayBuffer();
        } else if (pdfFile.filePath) {
          // File from storage - download
          const { data, error } = await supabase.storage
            .from('documents')
            .download(pdfFile.filePath);
          
          if (error || !data) {
            throw new Error(`Impossible de télécharger: ${pdfFile.name}`);
          }
          pdfBytes = await data.arrayBuffer();
        } else {
          throw new Error(`Fichier invalide: ${pdfFile.name}`);
        }
        
        // Load and copy pages
        const srcDoc = await PDFDocument.load(pdfBytes);
        const pages = await mergedPdf.copyPages(srcDoc, srcDoc.getPageIndices());
        pages.forEach(page => mergedPdf.addPage(page));
      }

      const mergedBytes = await mergedPdf.save();
      const bytes = mergedBytes instanceof Uint8Array ? mergedBytes : new Uint8Array(mergedBytes);
      const copy = new Uint8Array(bytes.byteLength);
      copy.set(bytes);
      const blob = new Blob([copy.buffer as unknown as ArrayBuffer], { type: 'application/pdf' });
      
      // === VALIDATE PDF ===
      if (blob.size === 0) {
        throw new Error("PDF généré vide");
      }
      
      // Check PDF header
      const headerBytes = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
      const header = String.fromCharCode(...headerBytes);
      if (!header.startsWith('%PDF')) {
        throw new Error("PDF invalide généré");
      }

      console.log('[PDFMerge] Local merge successful, pages:', mergedPdf.getPageCount(), 'size:', blob.size);
      
      // Debug: first bytes
      const firstBytes = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
      console.log('[PDFMerge] First 16 bytes:', Array.from(firstBytes));
      
      const fileName = `document-fusionne-${Date.now()}.pdf`;

      // Save to documents if option is checked
      let insertedDocId: string | undefined;
      let storedFilePath: string | undefined;
      
      if (saveToDocuments) {
        // 1. Upload to storage first
        storedFilePath = `${user.id}/${crypto.randomUUID()}.pdf`;
        const { error: uploadError } = await supabase.storage
          .from('documents')
          .upload(storedFilePath, blob);

        if (uploadError) throw uploadError;

        // 2. Call validate-document (SINGLE entry point for document creation)
        // This ensures consistent document creation + automatic queue enqueue
        const { data: validateResult, error: validateError } = await supabase.functions.invoke(
          'validate-document',
          {
            body: {
              fileName,
              fileSize: blob.size,
              mimeType: 'application/pdf',
              filePath: storedFilePath,
              source: 'upload',
              userId: user.id,
              title: 'Document fusionné'
            }
          }
        );

        if (validateError) {
          // Cleanup storage on failure
          await supabase.storage.from('documents').remove([storedFilePath]);
          throw new Error(validateError.message);
        }

        if (validateResult?.result === 'rejected') {
          await supabase.storage.from('documents').remove([storedFilePath]);
          throw new Error(validateResult.rejectionReason || 'Fichier rejeté');
        }

        insertedDocId = validateResult?.documentId;
        
        // NOTE: No triggerAIAnalysis() - the worker handles analysis via the queue
        // NOTE: No update({ status: 'complete' }) - the worker sets final status
        console.log('[PDFMerge] Document created via validate-document, worker will process:', insertedDocId);
      }

      // Store result for preview
      const resultUrl = URL.createObjectURL(blob);
      setMergedResult({
        blob,
        url: resultUrl,
        fileName,
        documentId: insertedDocId,
        filePath: storedFilePath
      });
      
      toast.success(`PDF fusionné (${mergedPdf.getPageCount()} pages)${saveToDocuments ? '. Analyse en cours...' : ''}`);

      await incrementUsage("pdf_actions");
      setFiles([]);
      fetchExistingDocs();
    } catch (error: any) {
      console.error('[PDFMerge] Error:', error);
      toast.error(error.message || "Erreur lors de la fusion des PDF");
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadMerged = () => {
    if (!mergedResult) return;
    const a = document.createElement('a');
    a.href = mergedResult.url;
    a.download = mergedResult.fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("PDF téléchargé");
  };

  const handleSignMerged = () => {
    if (!mergedResult || !onRequestSign) return;
    if (mergedResult.blob.size === 0) {
      toast.error("PDF invalide, impossible de signer");
      return;
    }
    onRequestSign(mergedResult.blob, mergedResult.fileName);
    // Clean up
    URL.revokeObjectURL(mergedResult.url);
    setMergedResult(null);
  };

  const handleOpenNewTab = () => {
    if (!mergedResult) return;
    window.open(mergedResult.url, '_blank');
  };

  const handleNewMerge = () => {
    if (mergedResult) {
      URL.revokeObjectURL(mergedResult.url);
    }
    setMergedResult(null);
    setFiles([]);
  };

  // Cleanup URL on unmount
  useEffect(() => {
    return () => {
      if (mergedResult?.url) {
        URL.revokeObjectURL(mergedResult.url);
      }
    };
  }, [mergedResult]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5" />
          {t.pdf.merge}
        </CardTitle>
        <p className="text-sm text-muted-foreground">{t.pdf.mergeDescription}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <UsageLimitBanner type="pdf_actions" showUpgrade={false} />
        
        {/* Show merged result preview */}
        {mergedResult ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                Document fusionné
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNewMerge}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Nouvelle fusion
              </Button>
            </div>
            
            {/* PDF Preview with SmartDocumentViewer */}
            <div className="h-[400px] md:h-[600px] w-full border rounded-lg overflow-hidden bg-muted/30">
              <SmartDocumentViewer 
                url={mergedResult.url}
                fileData={mergedResult.blob}
                title={mergedResult.fileName}
                mimeType="application/pdf"
                showControls={true}
                onError={(err) => console.error('[PDFMerge] Preview error:', err)}
              />
            </div>
            
            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleDownloadMerged}
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger
              </Button>
              <Button
                variant="outline"
                onClick={handleOpenNewTab}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Nouvel onglet
              </Button>
              {onRequestSign && (
                <Button
                  className="flex-1"
                  onClick={handleSignMerged}
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  Éditer ce document
                </Button>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground text-center">
              {mergedResult.fileName}
              {mergedResult.documentId && " • Sauvegardé dans vos documents"}
            </p>
          </div>
        ) : (
          <>
            {/* Source selection buttons */}
            {!canAddMoreDocs && maxMergeDocs !== -1 && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm text-amber-700 dark:text-amber-400">
                Limite de {maxMergeDocs} documents atteinte. Passez à un forfait supérieur pour fusionner plus de documents.
              </div>
            )}
        
            <div className="flex gap-2">
              <div
                className={`flex-1 border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                  canAddMoreDocs 
                    ? 'border-border hover:border-primary/50 hover:bg-primary/5 cursor-pointer' 
                    : 'border-muted-foreground/20 bg-muted/50 cursor-not-allowed opacity-60'
                }`}
                onClick={() => canAddMoreDocs && document.getElementById('pdf-merge-input')?.click()}
                onKeyDown={(e) => e.key === 'Enter' && canAddMoreDocs && document.getElementById('pdf-merge-input')?.click()}
                role="button"
                tabIndex={canAddMoreDocs ? 0 : -1}
              >
                <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm font-medium">Depuis l'appareil</p>
                <p className="text-xs text-muted-foreground">Importer des fichiers</p>
              </div>
              
              <Dialog open={docDialogOpen} onOpenChange={(open) => canAddMoreDocs && setDocDialogOpen(open)}>
                <DialogTrigger asChild>
                  <div 
                    className={`flex-1 border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                      canAddMoreDocs 
                        ? 'border-border hover:border-primary/50 hover:bg-primary/5 cursor-pointer' 
                        : 'border-muted-foreground/20 bg-muted/50 cursor-not-allowed opacity-60'
                    }`}
                    role="button"
                    tabIndex={canAddMoreDocs ? 0 : -1}
                  >
                    <FolderOpen className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm font-medium">Mes documents</p>
                    <p className="text-xs text-muted-foreground">Sélectionner existants</p>
                  </div>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Sélectionner des documents PDF</DialogTitle>
                  </DialogHeader>
                  {loadingDocs ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : existingDocs.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Aucun document PDF trouvé
                    </div>
                  ) : (
                    <>
                      <ScrollArea className="max-h-[300px]">
                        <div className="space-y-2">
                          {existingDocs.map(doc => (
                            <div 
                              key={doc.id}
                              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                                selectedDocs.includes(doc.id) ? 'bg-primary/10' : 'hover:bg-secondary/50'
                              }`}
                              onClick={() => toggleDocSelection(doc.id)}
                            >
                              <Checkbox 
                                checked={selectedDocs.includes(doc.id)}
                                onCheckedChange={() => toggleDocSelection(doc.id)}
                              />
                              <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center">
                                <FileText className="w-4 h-4 text-primary" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{doc.file_name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {formatFileSize(doc.file_size || 0)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                      <Button 
                        onClick={addSelectedDocs} 
                        disabled={selectedDocs.length === 0}
                        className="w-full"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Ajouter {selectedDocs.length > 0 && `(${selectedDocs.length})`}
                      </Button>
                    </>
                  )}
                </DialogContent>
              </Dialog>
            </div>
            
            <input
              id="pdf-merge-input"
              type="file"
              accept="application/pdf"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />

            {files.length > 0 && (
              <div className="space-y-2">
                {files.map((file, index) => (
                  <div
                    key={file.id}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-3 p-3 rounded-lg bg-secondary/30 transition-all ${
                      draggedIndex === index ? 'opacity-50 scale-95' : ''
                    }`}
                  >
                    <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
                    <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center">
                      <FileText className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {file.size}
                        {file.documentId && <span className="ml-2 text-primary">• EasyFinder</span>}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground w-8 text-center">{index + 1}</span>
                    <Button variant="ghost" size="icon" onClick={() => removeFile(file.id)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {files.length >= 2 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="save-to-docs" 
                    checked={saveToDocuments}
                    onCheckedChange={(checked) => setSaveToDocuments(checked as boolean)}
                  />
                  <label htmlFor="save-to-docs" className="text-sm cursor-pointer">
                    Sauvegarder dans mes documents
                  </label>
                </div>
                
                <Button 
                  className="w-full" 
                  onClick={handleMerge}
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t.pdf.processing}
                    </>
                  ) : saveToDocuments ? (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Fusionner et sauvegarder
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      {t.pdf.download}
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PDFMerge;
